import { createReducer, on } from '@ngrx/store';

import {
  ADVANCED_FILTER_TYPE,
  RESPONDENT_LIST_ORIGIN,
  SurveyLaunchFormMapper,
} from '@ps/survey-utils';

import {
  DEFAULT_SURVEY_CHILD_KEY,
  INITIAL_SURVEY_CREATION_LAUNCH_STATE,
} from '../survey-creation-state.constants';
import { SurveyCreationLaunchState } from '../survey-creation-state.model';
import { coreContextOf } from '../../utils/core-context/survey-creation-action-core-context.helper';
import {
  SurveyCreationCoreActions,
  SurveyCreationCoreApiActions,
  SurveyCreationCoreChildrenApiActions,
} from '../core/survey-creation-core.actions';
import {
  SurveyCreationAudienceActions,
  SurveyCreationMcApiActions,
} from '../audience/survey-creation-audience.actions';
import { omitChild } from '../audience/utils/audience-children.helper';
import {
  SurveyCreationLaunchActions,
  SurveyCreationLaunchApiActions,
} from './survey-creation-launch.actions';
import {
  initializeLaunchState,
  isLoadedLaunchSibling,
  patchChildFilterAudience,
  patchChildren,
  patchRespondentListUploads,
  registerLaunchChildren,
} from './utils/launch-children.helper';
import {
  CHILD_SCOPE,
  delegateRecordToDefaultChild,
} from '../survey-creation-child-registry.helper';

export const surveyCreationLaunchReducer = createReducer(
  INITIAL_SURVEY_CREATION_LAUNCH_STATE,

  // Cross-listens to both stage-specific init actions — the save pipeline
  // reads launch child state regardless of which page opened it.
  on(
    SurveyCreationAudienceActions.init,
    SurveyCreationLaunchActions.init,
    (state, action): SurveyCreationLaunchState => {
      const { surveyId } = action;
      const { rootSurveyId } = coreContextOf(action);

      const alreadyTargeted = !!surveyId && rootSurveyId === surveyId;

      if (alreadyTargeted) {
        return state;
      }

      // Legacy: a locale switch keeps the launch form and its "same …"
      // checkboxes; only the per-survey launch data starts over.
      if (!!surveyId && isLoadedLaunchSibling(state.children, surveyId)) {
        return {
          ...state,
          pricing: null,
          isPricingLoading: false,
          isLaunching: false,
          aidaEligibility: null,
        };
      }

      return initializeLaunchState(surveyId);
    },
  ),

  // The Launch page resets this slice when it is left, and every save — Define
  // Audience included — PUTs the ACTIVE child's launch form, so a child left at
  // its defaults would overwrite on the API what the Launch page saved. Both
  // survey loads therefore rebuild the child's value from the loaded survey
  // (the GET is the source of truth). A server sync, not a user edit: valid,
  // errors and isDirty stay as the child already holds them.
  on(
    SurveyCreationCoreChildrenApiActions.loadChildSurveysSuccess,
    (state, { surveys }): SurveyCreationLaunchState =>
      registerLaunchChildren(
        state,
        surveys.map(({ id }) => id),
      ),
  ),

  on(
    SurveyCreationMcApiActions.addSurveyChildSuccess,
    (state, { survey }): SurveyCreationLaunchState =>
      registerLaunchChildren(state, [survey.id]),
  ),

  on(
    SurveyCreationMcApiActions.deleteSurveyChildSuccess,
    (state, { surveyId }): SurveyCreationLaunchState => ({
      ...state,
      children: omitChild(state.children, surveyId),
      respondentListUploads: omitChild(state.respondentListUploads, surveyId),
    }),
  ),

  on(
    SurveyCreationLaunchActions.updateSameSettings,
    (state, { sameSettings }): SurveyCreationLaunchState => ({
      ...state,
      sameSettings,
    }),
  ),

  on(
    SurveyCreationCoreApiActions.loadRootSurveyForEditSuccess,
    (state, action): SurveyCreationLaunchState =>
      patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.keys([action.survey.id]),
        () => ({
          value: new SurveyLaunchFormMapper().fromSurvey(action.survey),
        }),
      ),
  ),

  // A standard or MC survey is its own active child; a project's active child
  // is a different survey than the root just seeded above.
  on(
    SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess,
    (state, action): SurveyCreationLaunchState =>
      patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        () => ({
          value: new SurveyLaunchFormMapper().fromSurvey(action.survey),
        }),
      ),
  ),

  on(
    SurveyCreationAudienceActions.applyAdvancedFilters,
    (state, action): SurveyCreationLaunchState =>
      patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        (child) =>
          patchChildFilterAudience(child, {
            includeExcludeRespondentsEnabled:
              action.filters.type === ADVANCED_FILTER_TYPE.INCLUDE,
          }),
      ),
  ),

  on(
    SurveyCreationCoreActions.resetActiveSurveyId,
    (state, action): SurveyCreationLaunchState => {
      const fromKey =
        coreContextOf(action).activeChildKey ?? DEFAULT_SURVEY_CHILD_KEY;
      return {
        ...state,
        children: delegateRecordToDefaultChild(state.children, fromKey),
        respondentListUploads: delegateRecordToDefaultChild(
          state.respondentListUploads,
          fromKey,
        ),
      };
    },
  ),

  on(
    SurveyCreationLaunchActions.updateForm,
    (state, action): SurveyCreationLaunchState =>
      patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        { ...action.form },
      ),
  ),

  // Server sync, not a user edit — validity, errors and dirtiness stay as the
  // child already holds them so an untouched launch form is never marked dirty.
  on(
    SurveyCreationLaunchActions.patchSurveyUrls,
    (state, action): SurveyCreationLaunchState =>
      patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          value: { ...child.value, surveyUrls: action.surveyUrls },
          valid: child.valid,
          errors: child.errors,
          isDirty: child.isDirty,
        }),
      ),
  ),

  on(
    SurveyCreationCoreApiActions.saveSurveySuccess,
    (state, action): SurveyCreationLaunchState =>
      patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          ...child,
          isDirty: false,
        }),
      ),
  ),

  on(
    SurveyCreationLaunchActions.loadLaunchPricing,
    (state): SurveyCreationLaunchState => ({
      ...state,
      pricing: null,
      isPricingLoading: true,
      isLaunching: false,
    }),
  ),

  on(
    SurveyCreationLaunchApiActions.launchPricingLoaded,
    (state, { pricing }): SurveyCreationLaunchState => ({
      ...state,
      pricing,
      isPricingLoading: false,
    }),
  ),

  on(
    SurveyCreationLaunchApiActions.launchPricingFailure,
    (state): SurveyCreationLaunchState => ({
      ...state,
      pricing: null,
      isPricingLoading: false,
    }),
  ),

  on(
    SurveyCreationLaunchActions.loadAidaEligibility,
    SurveyCreationLaunchApiActions.aidaEligibilityFailure,
    (state): SurveyCreationLaunchState => ({ ...state, aidaEligibility: null }),
  ),

  on(
    SurveyCreationLaunchApiActions.aidaEligibilityLoaded,
    (state, { eligibility }): SurveyCreationLaunchState => ({
      ...state,
      aidaEligibility: eligibility,
    }),
  ),

  on(
    SurveyCreationLaunchActions.launchSurvey,
    (state): SurveyCreationLaunchState => ({ ...state, isLaunching: true }),
  ),

  // A failed save ends the launch attempt too — the status change never runs.
  on(
    SurveyCreationLaunchApiActions.launchSurveySuccess,
    SurveyCreationLaunchApiActions.launchSurveyFailure,
    SurveyCreationCoreApiActions.saveSurveyFailure,
    (state): SurveyCreationLaunchState => ({ ...state, isLaunching: false }),
  ),
  on(
    SurveyCreationLaunchActions.uploadRespondentList,
    SurveyCreationLaunchActions.loadRespondentList,
    SurveyCreationLaunchActions.resolveRespondentFilters,
    (state, action): SurveyCreationLaunchState =>
      patchRespondentListUploads(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        { loading: true, error: null },
      ),
  ),

  on(
    SurveyCreationLaunchApiActions.uploadRespondentListSuccess,
    (state, action): SurveyCreationLaunchState =>
      patchRespondentListUploads(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        {
          loading: false,
          error: null,
          items: action.items,
          origin: RESPONDENT_LIST_ORIGIN.BUYER_UPLOAD,
        },
      ),
  ),

  on(
    SurveyCreationLaunchApiActions.loadRespondentListSuccess,
    (state, action): SurveyCreationLaunchState =>
      patchRespondentListUploads(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        {
          loading: false,
          error: null,
          items: action.items,
          origin: action.origin,
        },
      ),
  ),

  on(
    SurveyCreationLaunchApiActions.resolveRespondentFiltersSuccess,
    (state, action): SurveyCreationLaunchState =>
      patchRespondentListUploads(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        {
          loading: false,
          error: null,
          items: action.filters.psidRefs,
          origin: action.filters.psidRefs.length
            ? RESPONDENT_LIST_ORIGIN.SURVEY_GROUPING
            : null,
        },
      ),
  ),

  on(
    SurveyCreationLaunchApiActions.uploadRespondentListFailure,
    SurveyCreationLaunchApiActions.loadRespondentListFailure,
    SurveyCreationLaunchApiActions.resolveRespondentFiltersFailure,
    (state, action): SurveyCreationLaunchState =>
      patchRespondentListUploads(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        { loading: false, error: action.error },
      ),
  ),

  on(
    SurveyCreationLaunchActions.clearRespondentList,
    (state, action): SurveyCreationLaunchState =>
      patchRespondentListUploads(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        { loading: true, error: null },
      ),
  ),

  on(
    SurveyCreationLaunchApiActions.clearRespondentListSuccess,
    (state, action): SurveyCreationLaunchState =>
      patchRespondentListUploads(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        { loading: false, error: null, items: [], origin: null },
      ),
  ),

  on(
    SurveyCreationLaunchActions.restoreSavedGrouping,
    (state, action): SurveyCreationLaunchState =>
      patchRespondentListUploads(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        { loading: false, error: null, items: [], origin: null },
      ),
  ),

  on(
    SurveyCreationLaunchApiActions.clearRespondentListFailure,
    (state, action): SurveyCreationLaunchState =>
      patchRespondentListUploads(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        { loading: false, error: action.error },
      ),
  ),

  on(
    SurveyCreationCoreActions.reset,
    (): SurveyCreationLaunchState => INITIAL_SURVEY_CREATION_LAUNCH_STATE,
  ),
);
